import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, FileText, Eye, CheckCircle2, XCircle, Users, Image as ImageIcon, Video, Music, RefreshCw, Download } from 'lucide-react'
import { contentApi, type ContentStatsOverview, type ContentAudience } from '../../lib/api'

const mediaLabel: Record<string, { label: string; color: string }> = {
  text: { label: '图文', color: 'bg-blue-100 text-blue-700' },
  video: { label: '视频', color: 'bg-purple-100 text-purple-700' },
  audio: { label: '音频', color: 'bg-orange-100 text-orange-700' },
}

const StatCard = ({ label, value, Icon, color }: any) => (
  <div className="bg-white border rounded p-4 flex items-center gap-3">
    <div className={'rounded p-2 ' + color}><Icon className="w-5 h-5" /></div>
    <div>
      <div className="text-2xl font-semibold">{value ?? 0}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  </div>
)


const exportStatsCSV = (stats: ContentStatsOverview, days: number) => {
  const lines: string[] = []
  lines.push('section,key,value')
  lines.push(`summary,total,${stats.total}`)
  lines.push(`summary,draft,${stats.draft}`)
  lines.push(`summary,published,${stats.published}`)
  lines.push(`summary,offline,${stats.offline}`)
  lines.push(`summary,recent_views_${days}d,${stats.recent_views}`)
  for (const [k, v] of Object.entries(stats.by_category || {})) {
    lines.push(`category,${k.replace(/,/g, '/')},${v}`)
  }
  for (const [k, v] of Object.entries(stats.by_media_type || {})) {
    lines.push(`media_type,${k},${v}`)
  }
  for (const c of stats.top_contents || []) {
    const cat = (c.category_name || '').replace(/,/g, '/')
    lines.push(`top,${c.title.replace(/,/g, '/')},${c.media_type},${cat},${c.view_count}`)
  }
  // 加 BOM 让 Excel 识别 UTF-8
  const csv = '\ufeff' + lines.join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `content-stats-${days}d-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const ContentStatistics = () => {
  const [stats, setStats] = useState<ContentStatsOverview | null>(null)
  const [audiences, setAudiences] = useState<ContentAudience[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // 时间范围：7/30/90 天
  const [range, setRange] = useState<7 | 30 | 90>(7)
  // 内容维度筛选
  const [mediaFilter, setMediaFilter] = useState<'' | 'text' | 'video' | 'audio'>('')

  const load = async () => {
    setLoading(true); setError('')
    try {
      const [s, a] = await Promise.all([
        contentApi.adminStats({ days: range }),
        contentApi.adminAudiences().catch(() => []),
      ])
      setStats(s)
      setAudiences(a || [])
    } catch (e: any) { setError(e.message || '加载失败') }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [range])

  // 过滤后的内容总数
  const filteredTotal = useMemo(() => {
    if (!stats) return 0
    if (!mediaFilter) return stats.total
    const mt = stats.by_media_type || {}
    return mt[mediaFilter] || 0
  }, [stats, mediaFilter])

  if (loading) return <div className="p-6 text-gray-400">加载中…</div>
  if (error) return <div className="p-6 text-red-600">{error}</div>
  if (!stats) return null

  const max = Math.max(1, ...Object.values(stats.by_category || {}))

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          <h1 className="text-xl font-semibold">内容统计</h1>
        </div>
        <div className="flex items-center gap-2">
          <select value={range} onChange={e => setRange(Number(e.target.value) as any)} className="border rounded px-2 py-1 text-sm">
            <option value={7}>近 7 日</option>
            <option value={30}>近 30 日</option>
            <option value={90}>近 90 日</option>
          </select>
          <select value={mediaFilter} onChange={e => setMediaFilter(e.target.value as any)} className="border rounded px-2 py-1 text-sm">
            <option value="">全部媒体</option>
            <option value="text">图文</option>
            <option value="video">视频</option>
            <option value="audio">音频</option>
          </select>
          <button onClick={load} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" /> 刷新
          </button>
          <button onClick={() => exportStatsCSV(stats, range)} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded hover:bg-gray-50">
            <Download className="w-4 h-4" /> 导出 CSV
          </button>
        </div>
      </div>

      {/* 顶部指标 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="内容总数" value={filteredTotal} Icon={FileText} color="bg-blue-50 text-blue-600" />
        <StatCard label="草稿" value={stats.draft} Icon={FileText} color="bg-gray-50 text-gray-600" />
        <StatCard label="已发布" value={stats.published} Icon={CheckCircle2} color="bg-green-50 text-green-600" />
        <StatCard label="已下线" value={stats.offline} Icon={XCircle} color="bg-yellow-50 text-yellow-600" />
        <StatCard label={'近 ' + range + ' 日阅读'} value={stats.recent_views} Icon={Eye} color="bg-purple-50 text-purple-600" />
      </div>

      {/* 分类分布 + 媒体分布 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border rounded p-4">
          <h2 className="text-sm font-medium mb-3">按一级分类</h2>
          <div className="space-y-2">
            {Object.entries(stats.by_category || {}).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2">
                <span className="w-28 text-sm truncate">{k}</span>
                <div className="flex-1 bg-gray-100 rounded h-3 overflow-hidden">
                  <div className="bg-blue-500 h-full transition-all" style={{ width: max ? (v * 100 / max) + '%' : '0%' }} />
                </div>
                <span className="w-10 text-right text-xs">{v}</span>
              </div>
            ))}
            {Object.keys(stats.by_category || {}).length === 0 && <div className="text-xs text-gray-400">暂无数据</div>}
          </div>
        </div>

        <div className="bg-white border rounded p-4">
          <h2 className="text-sm font-medium mb-3">按媒体类型（已发布）</h2>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="border rounded p-3">
              <div className="text-2xl font-semibold">{stats.by_media_type.text || 0}</div>
              <div className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1"><FileText className="w-3 h-3" />图文</div>
            </div>
            <div className="border rounded p-3">
              <div className="text-2xl font-semibold">{stats.by_media_type.video || 0}</div>
              <div className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1"><Video className="w-3 h-3" />视频</div>
            </div>
            <div className="border rounded p-3">
              <div className="text-2xl font-semibold">{stats.by_media_type.audio || 0}</div>
              <div className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1"><Music className="w-3 h-3" />音频</div>
            </div>
          </div>
        </div>
      </div>

      {/* 热门内容排行 */}
      <div className="bg-white border rounded p-4">
        <h2 className="text-sm font-medium mb-3 flex items-center gap-1"><BarChart3 className="w-4 h-4" />热门内容 Top 10（按阅读量）</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="py-2 pr-3 w-10">#</th>
                <th className="py-2 pr-3">标题</th>
                <th className="py-2 pr-3 w-28">分类</th>
                <th className="py-2 pr-3 w-20">媒体</th>
                <th className="py-2 pr-3 w-24 text-right">阅读量</th>
              </tr>
            </thead>
            <tbody>
              {(stats.top_contents || []).length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-gray-400 text-xs">暂无已发布内容</td></tr>
              )}
              {(stats.top_contents || []).map((c, i) => (
                <tr key={c.id} className="border-b last:border-b-0 hover:bg-gray-50">
                  <td className="py-2 pr-3 text-gray-500">{i + 1}</td>
                  <td className="py-2 pr-3"><Link to={`/admin/content/${c.id}`} className="hover:text-blue-600">{c.title}</Link></td>
                  <td className="py-2 pr-3 text-gray-500">{c.category_name || '-'}</td>
                  <td className="py-2 pr-3">{mediaLabel[c.media_type]?.label || c.media_type}</td>
                  <td className="py-2 pr-3 text-right font-medium">{c.view_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 目标人群 */}
      <div className="bg-white border rounded p-4">
        <h2 className="text-sm font-medium mb-3 flex items-center gap-1"><Users className="w-4 h-4" />目标人群字典</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {audiences.length === 0 && <div className="text-xs text-gray-400">暂无人群字典</div>}
          {audiences.map(a => (
            <div key={a.id} className="border rounded p-3">
              <div className="flex items-center gap-2">
                <span className="font-medium">{a.name}</span>
                {a.is_active === 0 && <span className="text-xs text-gray-400 bg-gray-100 px-1 rounded">停用</span>}
              </div>
              <div className="text-xs text-gray-400 mt-1">编码：{a.code} · 排序 {a.sort_order}</div>
            </div>
          ))}
        </div>
        <div className="text-xs text-gray-400 mt-3">注：当前后端未返回按人群的内容分布，需要时可单独扩展统计接口。</div>
      </div>
    </div>
  )
}

export default ContentStatistics